



























import {
  ref,
  computed,
  useContext,
  useRoute,
  defineComponent,
  useFetch,
  onMounted,
} from '@nuxtjs/composition-api';
import { merge } from 'lodash-es';
import { useCache, CacheTagPrefix } from '@vue-storefront/cache';
import SfBreadcrumbs from '/storefrontUI/components/components/atoms/SfBreadcrumbs/SfBreadcrumbs.vue';
import SfLoader from '/storefrontUI/components/components/atoms/SfLoader/SfLoader.vue';
import { getBreadcrumbs } from '~/modules/catalog/product/getters/productGetters';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { usePageStore } from '~/stores/page';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import {useWishlist, useApi, useContent, useConfig} from '~/composables';
import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import type { Product } from '~/modules/catalog/product/types';
import type { ProductDetailsQuery, CmsPage } from '~/modules/GraphQL/types';
import ProductSkeleton from '~/modules/catalog/product/components/ProductSkeleton.vue';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';

export default defineComponent({
  name: 'ProductPage',
  components: {
    ProductSkeleton,
    SimpleProduct: () => import('~/modules/catalog/product/components/product-types/simple/SimpleProduct.vue'),
    BundleProduct: () => import('~/modules/catalog/product/components/product-types/bundle/BundleProduct.vue'),
    ConfigurableProduct: () => import('~/modules/catalog/product/components/product-types/configurable/ConfigurableProduct.vue'),
    GroupedProduct: () => import('~/modules/catalog/product/components/product-types/grouped/GroupedProduct.vue'),
    InstagramFeed: () => import('~/components/InstagramFeed.vue'),
    MobileStoreBanner: () => import('~/components/MobileStoreBanner.vue'),
    RelatedProducts: () => import('~/modules/catalog/product/components/RelatedProducts.vue'),
    UpsellProducts: () => import('~/modules/catalog/product/components/UpsellProducts.vue'),
    LoadWhenVisible,
    SfBreadcrumbs,
    SfLoader,
  },
  transition: 'fade',
  setup() {
    const { routeData } = usePageStore();
    const { query } = useApi();
    const product = ref<Product | null>(null);
    const { addTags } = useCache();
    const { localePath } = useContext();
    const { loadPage } = useContent();
    const route = useRoute();
    const { getProductDetails, loading } = useProduct();
    const { error: nuxtError } = useContext();
    const { load: loadWishlist } = useWishlist();
    const home = ref<CmsPage | null>(null);
    const { config } = useConfig();
    const swatchesProducts = ref([]);
    const {getProductList} = useProduct();
    const aggregationsProducts = ref([]);
    const aggregationsProductsUp = [];
    const mgs_brandUp = [];
    const breadcrumbs = computed(() => {
      const productCategories = product.value?.categories ?? [];

      return getBreadcrumbs(
        product.value,
        Array.isArray(productCategories) ? [...productCategories][2] : null,
      ).map((breadcrumb) => ({ ...breadcrumb, link: localePath(breadcrumb.link) }));
    });

    const logoSrc = computed(() => {
      const baseMediaUrl = config.value.base_media_url;
      const logo = config.value.header_logo_src;

      return baseMediaUrl && logo ? `${baseMediaUrl}logo/${logo}` : '';
    });

    const getBaseSearchQuery = () => ({
      filter: {
        sku: {
          eq: routeData.sku,
        },
      },
      configurations: Object.entries(route.value.query)
        .filter((config) => config[0] !== 'wishlist')
        .map(
          (config) => config[1],
        ) as string[],
    });

    // eslint-disable-next-line no-underscore-dangle
    const renderer = computed(() => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT);

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      const result = await getProductDetails({
        ...searchQuery,
      });

      product.value = merge({}, product.value, result.items[0] as Product ?? null);
    };

    const fetchProductExtendedData = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await query<ProductDetailsQuery>(getProductPriceBySkuGql, searchQuery);

      product.value = merge({}, product.value, data.products?.items?.[0] as Product);
    };

    const url = route?.value.fullPath;

    const mgs_brand = computed(() => { mgs_brandUp });

    useFetch(async () => {
      home.value = await loadPage({ identifier: 'home' });
      await fetchProductBaseData();

      if (Boolean(product?.value?.sku) === false) nuxtError({ statusCode: 404 });

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${routeData.sku}`,
        },
      ];

      const productTags = [{
        prefix: CacheTagPrefix.Product,
        value: product.value.uid,
      }];


      let sku = product?.value?.sku.split("-");

      const swatches = await getProductList({
        pageSize: 24,
        search: sku[0]
      });

      if (swatches?.items?.length) {
        swatchesProducts.value = swatches.items;
      }

      if (swatches?.aggregations?.length) {
        let excludes = ['price', 'category_id', 'size'];
        for(var key in swatches.aggregations) {
          if (swatches.aggregations.hasOwnProperty(key)) {
            if(!excludes.includes(swatches.aggregations[key].attribute_code) ){
              aggregationsProductsUp.push(swatches.aggregations[key]);
            }
            if(swatches.aggregations[key].attribute_code == 'mgs_brand'){
              mgs_brandUp.push(swatches.aggregations[key].options[0].label);
            }
            if(swatches.aggregations[key].attribute_code == 'color_set'){
              for(let i = 0; i < swatches.aggregations[key].options.length; i++){
                if(swatches.aggregations[key].options[i].value != product?.value?.color_set){
                  delete swatches.aggregations[key].options[i].label;
                }
              }
            }
          }
        }
        aggregationsProducts.value = aggregationsProductsUp;
      }


      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      addTags([...tags, ...productTags]);
    });


    onMounted(async () => {
      await Promise.all([fetchProductExtendedData(), loadWishlist()]);
    });

    return {
      renderer,
      loading,
      breadcrumbs,
      product,
      logoSrc,
      url,
      home,
      swatchesProducts,
      aggregationsProducts,
      mgs_brand,
      fetchProduct: fetchProductExtendedData,
    };
  },
  head() {
    return getMetaInfo(this.product, false, this.logoSrc, 'https://www.needstore.it' + this.url, this.home, 'product');
  },
});
